import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/auth';
import { dailyTasksApi } from '@/api/dailyTasks';
import { taskLinkFor } from '@/lib/taskLinks';
import { useHasFeature } from '@/hooks/useHasFeature';
import { progressApi } from '@/api/progress';
import { cefrApi } from '@/api/cefr';
import { mockApi } from '@/api/ieltsMock';
import { canAskIeltsProfile, STORE_TRAINER_KEYS } from '@/lib/storeTabs';
import { useFeatureCheck } from '@/hooks/useHasFeature';
import { useWeekEvents } from '@/hooks/useWeekEvents';
import { isPast } from '@/lib/calendarItems';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useAiSkills } from '@/hooks/useAiSkills';
import { withoutClosed } from '@/lib/aiEntries';
import type { FeatureKey } from '@/api/auth';
import { vocabApi } from '@/api/vocab';
import { wotdApi } from '@/api/wotd';
import { notificationsApi } from '@/api/notifications';
import { usersApi } from '@/api/users';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Mascot } from '@/components/ui/Mascot';
import { ContinueLearningCard } from '@/components/learning/ContinueLearningCard';
import { lessonPosition } from '@/lib/marathonCards';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MobileHome } from './MobileHome';
import { MaterialsWidget } from './MaterialsWidget';
import { StoryBarSlot } from './StoryBarSlot';
import { WordOfTheDayCard } from './WordOfTheDayCard';
import { StudentTopBar } from '@/components/layout/StudentTopBar';
import { WeekCard } from './desktop/WeekCard';

/**
 * Student home — mobile-first single column matching the approved redesign:
 * greeting → quick actions → streak → word of the day → current course →
 * CEFR progress → today's tasks → skills grid → recommended. All data is live
 * except the Word of the Day (client-side daily rotation until the backend
 * feature lands — see the word-of-the-day task).
 */

// Плитки быстрого перехода.
//
// Ведут в БЛОЧНЫЙ ТРЕНАЖЁР, а не в каталоги полных тестов. Раньше здесь
// стояли старые адреса /app/ielts/<навык>: ученик попадал в список тестов
// Cambridge и выбирал вручную — без подбора по слабым типам и без гейта
// «сперва мок». Хаб тренажёров переключили 13.08, а плитки главной остались
// от прежней реализации.
//
// Подписи берутся из переводов: раньше первая была вшита строкой «Оқу» —
// казахское слово в англоязычном интерфейсе.
// Плитка «все тренажёры» открыта, если открыт хотя бы один из них.
// Список ключей — один на вкладку, пункт меню, маршрут и эту плитку (`lib/storeTabs.ts`).
const ANY_TRAINER = STORE_TRAINER_KEYS satisfies readonly FeatureKey[];

//
// ⚠️ У КАЖДОЙ ПЛИТКИ СВОЙ КЛЮЧ ТАРИФА (04.09.2026, требование заказчика: «на
// мейн экране если тумблеры выключены — кнопки не должны быть»). До этого дня
// главная показывала все восемь плиток кому угодно: ученик Pre-Upper видел на
// первом же экране тренажёр речи, которого у него нет, нажимал и упирался в
// отказ. Плитки без ключа (уроки, материалы) доступны всем.
const SKILLS = [
  { to: '/app/lessons', icon: 'menu_book', key: 'dashboard.tiles.lessons', tone: 'primary' },
  { to: '/app/trainers/ielts/reading', icon: 'chrome_reader_mode', key: 'trainersHub.ieltsReading', tone: 'info', feature: 'ielts_reading' },
  { to: '/app/trainers/ielts/listening', icon: 'headphones', key: 'trainersHub.ieltsListening', tone: 'primary', feature: 'ielts_listening' },
  { to: '/app/trainers/ielts/speaking', icon: 'record_voice_over', key: 'trainersHub.ieltsSpeaking', tone: 'info', feature: 'ielts_speaking' },
  { to: '/app/trainers/ielts/writing', icon: 'edit', key: 'trainersHub.ieltsWriting', tone: 'primary', feature: 'ielts_writing' },
  { to: '/app/trainers', icon: 'smart_toy', key: 'dashboard.tiles.allTrainers', tone: 'info', feature: ANY_TRAINER },
  // Плитка «Материалдар» — из макета; страница появилась 24.08.2026.
  { to: '/app/materials', icon: 'video_library', key: 'materials.title', tone: 'primary' },
  // Мок-тест выделен голубой плиткой — в макете он последний и заметный.
  { to: '/app/ielts/mock', icon: 'star', key: 'dashboard.tiles.mock', tone: 'info', accent: true, feature: 'mock' },
] as const;


// Placeholder Word of the Day pool (rotates by day). Replaced by the shared,
// AI-generated, no-repeat word from the backend once that feature is built.

/*
 * Список дел приходит С СЕРВЕРА: задания задаёт куратор своей группе, ученику
 * или всему марафону, с периодичностью (например, слова по пн/ср/пт).
 *
 * ⚠️ ДО 01.09.2026 ЗДЕСЬ БЫЛ ХАРДКОД из трёх пунктов — одни и те же у всех и
 * каждый день, — а галочки лежали в памяти браузера: отметил на телефоне, на
 * ноутбуке пусто; почистил браузер — пропали; куратор не видел их вовсе.
 *
 * ⚠️ ПУСТОЙ ДЕНЬ ПРЯЧЕТ БЛОК ЦЕЛИКОМ (решение владельца 01.09.2026). Заголовок
 * «Задания на сегодня» над пустотой читается как поломка.
 */

export function StudentDashboardPage() {
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isMobile = useIsMobile();

  // ⚠️ СТОРИС НА ГЛАВНОЙ — ТОЛЬКО ЧЕРЕЗ `StoryBarSlot` (сторис 2.0, 13.10.2026): кружки, «Актуальное», плеер и
  // запрос ленты живут в ленивом куске и грузятся лишь при ключе `stories` в `sections`. Здесь их запросов и строк
  // нет намеренно — главную получает и бесплатник. До 13.10 тут стояли запрос и плеер, а в приложении их вырезала
  // ветка `IS_APP_BUILD` (D61); кнопка «скрыть истории» в шапке убрана вместе с прежней лентой.
  const stats = useQuery({ queryKey: ['student', 'stats'], queryFn: progressApi.getMyStats });
  // Only IELTS-style tracks have a CEFR level / mock. Beginner & Pre-Upper don't
  // (cefr feature off) → they keep the vocabulary card in the level slot. IELTS
  // students who haven't taken the mock get a "point A" nudge instead of a fake level.
  // В приложении CEFR нет (26.09.2026, сборка 61): на месте уровня — карточка словаря, как у ученика без cefr.
  const hasCefr = useHasFeature('cefr') && !IS_APP_BUILD;
  // ⚠️ БЕЗ ДОСТУПА ЗАПРОС НЕ ШЛЁМ (19.09.2026, договор `selling-words-guard.md` §8.3). Раньше главная
  // звала CEFR всем, и ученику без этой возможности прилетал отказ — тот самый 403, который ревьюер
  // Apple видел в трафике (3.1.1). Теперь сервер отвечает 200 с пустыми данными, но лишний запрос
  // на каждой главной незачем: уровень всё равно показывается только при `hasCefr`.
  const level = useQuery({
    queryKey: ['student', 'cefr-level'],
    queryFn: cefrApi.getProgress,
    enabled: hasCefr,
  });
  // The CEFR level is only real once the diagnostic/mock has run — before that we
  // show a "take the diagnostic" prompt instead of a fake A1 / 0% progress bar.
  // В приложении — только при разделе тренажёров (lib/storeTabs.ts, `canAskIeltsProfile`); на сайте — всегда.
  const ieltsProfile = useQuery({
    queryKey: ['ielts', 'profile'],
    queryFn: mockApi.profile,
    enabled: canAskIeltsProfile(user),
  });
  const mockDone = !!ieltsProfile.data?.mock_done;
  // Плитки, закрытые тарифом, на главную не попадают вовсе — не замком, а
  // отсутствием: «то, что недоступно в тарифах, просто прячем».
  const can = useFeatureCheck();
  // Закрытые входы (AI speaking, D22) уходят тем же отбором — и с компьютера, и
  // с телефонной главной: она получает этот же список.
  useAppConfig();
  // И тумблеры навыков ИИ-заданий (docs/CONTRACTS/ai-skills.md); ссылки заданий
  // ниже (taskLinkFor) отбираются тем же правилом.
  useAiSkills();
  const tiles = withoutClosed(SKILLS).filter((sk) => {
    // ⚠️ В ПРИЛОЖЕНИИ плитки без ключа («Уроки», «Материалы») — под разделом `lessons` (27.09.2026, техдолг 317):
    // нет раздела обучения — нет и входов в него, как нет вкладки «Обучение» (lib/storeTabs.ts). На сайте — как было.
    if (!('feature' in sk)) return IS_APP_BUILD ? can('lessons') : true;
    const keys = Array.isArray(sk.feature) ? sk.feature : [sk.feature];
    return keys.some((k) => can(k));
  });
  // Word of the day drives the bottom row layout: while it's live it sits next to
  // Practice as two squares; once studied it disappears and Practice goes full width.
  const wotd = useQuery({ queryKey: ['student', 'wotd'], queryFn: wotdApi.getToday });
  // ⚠️ СЛОВО ДНЯ ПОКАЗЫВАЕТСЯ ВСЕМ, В ТОМ ЧИСЛЕ ДО СТАРТА (решение заказчика
  // 05.09.2026: «пусть показывается до старта — пофиг»).
  //
  // Произношение сервер отдаёт только по ключу `vocab`, а до старта у учеников
  // один `lessons`. Немой кнопки при этом не будет: она появляется, ТОЛЬКО
  // когда запись действительно скачалась — см. useWordAudio. Само слово,
  // перевод и игра со словами работают и без звука.
  const wotdVisible = !!wotd.data?.word && !wotd.data.studied;
  // Vocabulary progress fills the level slot for tracks without a CEFR level
  // (Beginner / Pre-Upper) and before the IELTS mock is taken.
  const vocabStats = useQuery({ queryKey: ['vocab', 'stats'], queryFn: vocabApi.getStats });
  const unread = useQuery({ queryKey: ['student', 'unread'], queryFn: notificationsApi.unreadCount });
  // NB: wrap the call — `myEnrolledMarathons` uses `this`, so passing the bare
  // method reference as queryFn loses the binding and throws (blank dashboard
  // until another page populates the shared cache). Keep it invoked on usersApi.
  const marathons = useQuery({ queryKey: ['student', 'enrolled-marathons'], queryFn: () => usersApi.myEnrolledMarathons() });
  // ⚠️ ОСНОВНОЙ курс, а не первый в списке. Блок «Продолжить обучение» — самое
  // видное место главной, и с мини-курсами в тарифе сюда мог бы попасть курс по
  // психологии вместо языкового. Та же правка, что на экране «Обучение».
  //
  // Запасной вариант нужен: у только что зарегистрировавшегося есть лишь
  // бесплатный курс, записи на который нет, и основного среди них не будет.
  const current = marathons.data?.find((m) => m.is_primary) ?? marathons.data?.[0];
  const marProg = useQuery({
    queryKey: ['student', 'marathon-progress', current?.id],
    queryFn: () => progressApi.getMyMarathonProgress(current!.id),
    enabled: !!current,
  });
  const marathonIds = (marathons.data ?? []).map((m) => m.id);
  const dailyTasks = useQuery({
    queryKey: ['student', 'daily-tasks', marathonIds],
    queryFn: () => dailyTasksApi.today({ marathonIds }),
  });
  const tasks = dailyTasks.data ?? [];
  const doneTasks = tasks.filter((x) => x.done).map((x) => x.id);
  // Сегодняшние личные записи календаря — в «Задачах на сегодня», после заданий куратора (02.10.2026).
  // Запросы те же, что у карточки недели: общий кэш, второй волны нет.
  const { today, personalToday } = useWeekEvents();

  const toggleTask = async (id: string) => {
    // Отметка уходит на сервер: её должен видеть куратор и другое устройство
    // того же ученика. Ошибку глотаем — список перечитается сам.
    try {
      await dailyTasksApi.toggle(id);
    } finally {
      void dailyTasks.refetch();
    }
  };


  const firstName = user?.full_name?.split(' ')[0] ?? t('dashboard.greetingFallback');
  const streak = stats.data?.current_streak ?? 0;
  const pct = current ? Math.round(marProg.data?.percent_complete ?? 0) : 0;
  // «Урок N из M» — номер урока, на котором человек СЕЙЧАС, а не число пройденных:
  // «Lesson 0 of 5» — это не по-русски и не по-английски. В макете тоже номер текущего.
  const position = lessonPosition(marProg.data, current?.lesson_count ?? 0);

  const vLearned = vocabStats.data?.learned ?? 0;
  const vTotal = vocabStats.data?.total ?? 0;
  const vLearning = Math.max(vTotal - vLearned, 0);
  const vPct = vTotal > 0 ? Math.round((vLearned / vTotal) * 100) : 0;

  // "My progress" level. The MOCK is the source of truth for the student's
  // assessed level — cefr-service only tracks lesson-based progress and starts
  // empty (defaults to A1 / 0%) until lessons are completed. So show the mock's
  // level + per-skill bands; use cefr's percent/lessons-to-next ONLY when cefr is
  // already in sync with the mock level (otherwise a fresh mock would read as A1).
  const CEFR_LADDER = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;
  const nextCefr = (lvl?: string) => {
    const i = CEFR_LADDER.indexOf((lvl ?? '').toUpperCase() as (typeof CEFR_LADDER)[number]);
    return i >= 0 && i < CEFR_LADDER.length - 1 ? CEFR_LADDER[i + 1] : 'C2';
  };
  const mockLevel = ieltsProfile.data?.level || level.data?.current_level || 'A1';
  const cefrInSync = !!level.data && level.data.current_level === mockLevel;
  const progPct = cefrInSync ? level.data!.percent_to_next : 0;

  // Плитки практики — сетка 4×2 из макета. Цвет значка чередуется,
  // последняя плитка (мок-тест) выделена голубым.
  const practiceTiles = (
    <div className="grid grid-cols-4 gap-[9px]">
      {tiles.map((s) => (
        <Link
          key={s.key}
          to={s.to}
          className={cn(
            'flex flex-col items-center gap-[7px] rounded-[15px] px-2 py-[11px] transition',
            'accent' in s && s.accent
              ? 'bg-info-container hover:brightness-[0.97]'
              : 'bg-surface-container hover:bg-surface-container-high',
          )}
        >
          <span
            className={cn(
              'material-symbols-outlined text-[19px]',
              s.tone === 'info' ? 'text-info' : 'text-primary',
            )}
          >
            {s.icon}
          </span>
          <small className="text-center text-[11px] font-semibold leading-[1.2] text-on-surface">{t(s.key)}</small>
        </Link>
      ))}
    </div>
  );

  // Телефон получает СВОЮ раскладку по макету от 23.08.2026: расписание
  // недельной строкой, задачи одним блоком, упражнения лентой. Данные к этому
  // моменту уже загружены выше — второй волны запросов не будет.
  if (isMobile) {
    return (
      <MobileHome
        name={firstName}
        avatarUrl={user?.avatar_url}
        streak={streak}
        unread={unread.data?.count ?? 0}
        course={
          current
            ? {
                id: current.id,
                title: current.title,
                lessonNumber: position.number,
                lessonsTotal: position.total,
                percent: pct,
              }
            : undefined
        }
        tasks={tasks.map((task) => ({
          id: task.id,
          // В приложении — только туда, куда ученик попадёт (`lib/taskLinks.ts`); нет перехода — пустая строка.
          to: taskLinkFor(task, user) ?? '',
          title: task.title,
          meta: '',
          done: task.done,
        }))}
        onToggleTask={toggleTask}
        tiles={tiles.map((sk) => ({ to: sk.to, icon: sk.icon, label: t(sk.key) }))}
      />
    );
  }

  return (
    <div>
      <StudentTopBar
        title={t('dashboard.greeting', { name: firstName })}
        subtitle={t('dashboard.subtitle')}
        streak={streak}
        unread={unread.data?.count ?? 0}
        name={user?.full_name}
        avatarUrl={user?.avatar_url}
      />

      {/* Кружки историй и «Актуальное» — ленивым куском, только у ученика с ключом `stories` (StoryBarSlot). */}
      <StoryBarSlot variant="desktop" />

      {/* Две колонки: слева учёба, справа неделя, задачи и слово дня. */}
      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          {/* ── Продолжить обучение ─────────────────────────────────────── */}
          {/* Разметка блока — общая с телефоном и страницей курса (11.09.2026). */}
          {current ? (
            <ContinueLearningCard
              variant="desktop"
              title={current.title}
              coverUrl={current.cover_url}
              lessonNumber={position.number}
              lessonsTotal={position.total}
              percent={pct}
              continueTo="/app/lessons"
              allLessons={{ count: position.total, to: '/app/lessons' }}
            />
          ) : marathons.isLoading ? (
            // Пока курс грузится — заглушка, а не приглашение выбрать курс.
            <div className="min-h-[238px] animate-pulse rounded-[26px] bg-surface-container" />
          ) : marathons.isError ? (
            // Сбой загрузки — НЕ то же самое, что «курса нет».
            //
            // Раньше здесь показывалось «Выбери свой курс»: любая заминка на
            // сервере — перезапуск сервиса, обрыв связи — выглядела для ученика
            // как отчисление с марафона. Заказчик прислал ровно такой снимок
            // 17.08.2026, застав выкладку. Курс при этом был на месте.
            <Card className="flex items-center justify-between gap-3">
              <div>
                <h3 className="font-display text-lg font-bold">{t('dashboard.loadFailedTitle')}</h3>
                <p className="text-sm text-on-surface-variant">{t('dashboard.loadFailedText')}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => void marathons.refetch()}>
                <span className="material-symbols-outlined text-base">refresh</span>
                {t('common.retry')}
              </Button>
            </Card>
          ) : (
            <Link to="/app/lessons" className="block">
              <Card className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="font-display text-lg font-bold">{t('dashboard.startTitle')}</h3>
                  <p className="text-sm text-on-surface-variant">{t('dashboard.subtitle')}</p>
                </div>
                <Mascot pose="hello" className="h-20 w-auto object-contain" />
              </Card>
            </Link>
          )}

          {/* ── Полоска уровня ──────────────────────────────────────────────
              Курсы с уровнем (IELTS): после мока — сам уровень, до него —
              приглашение его пройти, но НЕ придуманный A1. Курсы без уровня
              (Beginner, Pre-Upper) держат здесь словарь. */}
          {hasCefr ? (
            mockDone ? (
              <LevelStrip
                to="/app/profile"
                icon="trending_up"
                title={t('dashboard.myProgress')}
                subtitle={t('dashboard.levelLabel', { level: mockLevel })}
                percent={progPct}
                action={`${mockLevel} → ${nextCefr(mockLevel)}`}
              />
            ) : ieltsProfile.data ? (
              <LevelStrip
                to="/app/ielts/mock"
                icon="leaderboard"
                title={t('dashboard.pointATitle')}
                subtitle={t('dashboard.pointASub')}
                percent={14}
                action={t('dashboard.pointABtn')}
                arrow
              />
            ) : null
          ) : (
            <LevelStrip
              to="/app/vocabulary"
              icon="menu_book"
              title={t('dashboard.myVocab')}
              subtitle={t('dashboard.vocabStat', { learned: vLearned, learning: vLearning })}
              percent={vPct}
              action={`${vPct}%`}
            />
          )}

          {/* ── Практика ────────────────────────────────────────────────── */}
          <div className="mx-0.5 mb-2.5 mt-5 flex items-baseline justify-between">
            <h2 className="font-display text-[15.5px] font-bold text-on-surface">{t('dashboard.practiceTitle')}</h2>
            <span className="text-[12.5px] text-on-surface-variant">{t('dashboard.practiceHint')}</span>
          </div>
          {practiceTiles}

          {/* ── Рекомендуем ─────────────────────────────────────────────── */}
          <MaterialsWidget title={t('dashboard.recommended')} layout="grid" />
        </div>

        {/* ── Правая колонка ───────────────────────────────────────────── */}
        <div className="space-y-[14px]">
          {/* Карточка недели есть и в приложении: календарь вернулся в сборку 1.1.1 (02.10.2026). */}
          <WeekCard />

          {/* Задания на сегодня. Блок ПРЯЧЕТСЯ ЦЕЛИКОМ, когда заданий нет:
              заголовок над пустотой читается как поломка.
              ⚠️ Сегодняшние личные записи календаря — здесь же, после заданий куратора
              (02.10.2026). Без галочки: отметки «сделано» у личных записей сервер не
              хранит. Счётчик считает только задания с галочкой. */}
          {(tasks.length > 0 || personalToday.length > 0) && (
            <div>
              <div className="mx-0.5 mb-2.5 mt-[18px] flex items-baseline justify-between">
                <h2 className="font-display text-[15.5px] font-bold text-on-surface">{t('dashboard.tasksTitle')}</h2>
                {tasks.length > 0 && (
                  <span className="text-[12.5px] font-semibold text-on-surface-variant">
                    {t('dashboard.tasksDone', { done: doneTasks.length, total: tasks.length })}
                  </span>
                )}
              </div>
              <div className="rounded-[20px] bg-surface-container px-[18px] py-0.5">
                {tasks.map((task) => {
                  const to = taskLinkFor(task, user);
                  const body = (
                    <>
                      <span
                        role="checkbox"
                        aria-checked={task.done}
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          void toggleTask(task.id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            void toggleTask(task.id);
                          }
                        }}
                        className={cn(
                          'grid h-[22px] w-[22px] flex-none place-items-center rounded-full border-[2.75px] transition',
                          task.done ? 'border-info bg-info text-on-primary' : 'border-info text-transparent',
                        )}
                      >
                        <span className="material-symbols-outlined text-[13px]">check</span>
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className={cn('block text-[13px] font-semibold text-on-surface', task.done && 'line-through opacity-60')}>
                          {task.title}
                        </span>
                      </span>
                      {to && <span className="material-symbols-outlined flex-none text-[15px] text-on-surface-variant">chevron_right</span>}
                    </>
                  );
                  return to ? (
                    // У задания-строки перехода может не быть: «повторить
                    // глаголы» — это напоминание, а не экран. Ссылка в никуда
                    // хуже её отсутствия.
                    <Link key={task.id} to={to} className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
                      {body}
                    </Link>
                  ) : (
                    <div key={task.id} className="flex items-center gap-3 border-b border-outline py-3 last:border-b-0">
                      {body}
                    </div>
                  );
                })}
                {personalToday.map((e) => (
                  <Link
                    key={e.id}
                    to="/app/schedule"
                    className={cn(
                      'flex items-center gap-3 border-b border-outline py-3 last:border-b-0',
                      isPast(e, today) && 'opacity-50',
                    )}
                  >
                    {/* На месте галочки — время: запись не отмечается, а напоминает. */}
                    <span className="w-9 flex-none text-center text-[11px] font-bold tabular-nums text-info">
                      {e.start.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-on-surface">{e.title}</span>
                    <span className="material-symbols-outlined flex-none text-[15px] text-on-surface-variant">chevron_right</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {wotdVisible && <WordOfTheDayCard tone="info" />}
        </div>
      </div>
    </div>
  );
}

/**
 * Полоска уровня под карточкой курса (макет «Desktop Web v2»): значок,
 * заголовок с подписью, тонкая шкала и действие справа.
 */
function LevelStrip({
  to,
  icon,
  title,
  subtitle,
  percent,
  action,
  arrow,
}: {
  to: string;
  icon: string;
  title: string;
  subtitle: string;
  percent: number;
  action: string;
  arrow?: boolean;
}) {
  return (
    <Link to={to} className="mt-3 flex items-center gap-3.5 rounded-[20px] bg-surface-container px-5 py-[15px]">
      <span className="grid h-[38px] w-[38px] flex-none place-items-center rounded-xl bg-primary-container">
        <span className="material-symbols-outlined text-[19px] text-primary">{icon}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-bold text-on-surface">{title}</span>
        <span className="mt-[3px] block truncate text-[12.5px] text-on-surface-variant">{subtitle}</span>
        <span className="mt-[9px] block h-[5px] rounded-full bg-outline">
          <span className="block h-[5px] rounded-full bg-primary transition-[width] duration-500" style={{ width: `${percent}%` }} />
        </span>
      </span>
      <span className="flex flex-none items-center gap-[7px] text-[12.5px] font-bold text-on-primary-container">
        {action}
        {arrow && <span className="material-symbols-outlined text-[15px]">arrow_forward</span>}
      </span>
    </Link>
  );
}
